import { Calendar } from 'iconoir-react';
import { useEffect } from 'react';

import { BrandMarkIcon } from '@superfunnel/ui-core/BrandMarkIcon';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';

import { openSalesCalendly } from '../components/Common/AiCreditsCard';
import { PromptModalBody } from '../components/Common/PromptModalBody';
import { useAnalytics } from '../contexts/AnalyticsContext';

export type PageLimitMetric =
  'pages' | 'publishedPages' | 'quizzes' | 'publishedQuizzes';

export interface PageLimit {
  metric: PageLimitMetric;
  current: number;
  limit: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  pageLimit: PageLimit;
}

const PAGE_LIMIT_COPY: Record<
  PageLimitMetric,
  { title: string; singular: string; plural: string; action: string }
> = {
  pages: {
    title: 'Page limit reached',
    singular: 'page',
    plural: 'pages',
    action: 'This would go over',
  },
  publishedPages: {
    title: 'Live page limit reached',
    singular: 'live page',
    plural: 'live pages',
    action: 'Publishing this would go over',
  },
  quizzes: {
    title: 'Quiz limit reached',
    singular: 'quiz',
    plural: 'quizzes',
    action: 'This would go over',
  },
  publishedQuizzes: {
    title: 'Live quiz limit reached',
    singular: 'live quiz',
    plural: 'live quizzes',
    action: 'Publishing this would go over',
  },
};

function getDescription({ metric, limit }: PageLimit) {
  const copy = PAGE_LIMIT_COPY[metric];
  const reason =
    limit === 0
      ? `Your current plan doesn't include ${copy.plural}.`
      : `${copy.action} the ${limit} ${limit === 1 ? copy.singular : copy.plural} included in your plan.`;
  return (
    <>
      {reason}
      <br />
      Contact us to update your plan.
    </>
  );
}

export function PageLimitModal({ open, onClose, pageLimit }: Props) {
  const { track } = useAnalytics();

  useEffect(() => {
    if (!open) return;

    track('page_limit_dialog_opened', { ...pageLimit });
  }, [open, pageLimit, track]);

  const trackAction = (action: 'book_call' | 'keep_limit') => {
    track('page_limit_dialog_action_clicked', {
      metric: pageLimit.metric,
      action,
    });
  };

  const bookCall = () => {
    trackAction('book_call');
    openSalesCalendly();
    onClose();
  };

  const keepLimit = () => {
    trackAction('keep_limit');
    onClose();
  };

  const actions: ModalActionData[] = [
    { type: 'secondary', title: 'Keep current limit', onClick: keepLimit },
    { type: 'primary', title: 'Book a call', onClick: bookCall },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="md"
      actions={actions}
      scrollableContent={false}
    >
      <PromptModalBody
        leadingTile={
          <BrandMarkIcon className="size-8" aria-label="SuperFunnel" />
        }
        trailingTile={<Calendar className="size-7 text-brand-500" />}
        title={PAGE_LIMIT_COPY[pageLimit.metric].title}
        description={getDescription(pageLimit)}
      />
    </Modal>
  );
}
